import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ChatConversation,
  ChatMessageRecord,
  SendChatMessageRequest,
  MarkReadRequest,
} from "../../types";
import {
  GetChatInbox,
  GetChatMessages,
  SendChatMessage,
  MarkMessagesRead,
  GetUnreadChatCount,
} from "../../services/endpoints/chat.endpoint";
import { GetProfile } from "../../services/endpoints/profile.endpoint";
import { storageService } from "../../services/storage.service";
import { logout } from "./authSlice";
import type { RootState } from "../store";

export interface ChatState {
  conversations: ChatConversation[];
  activeOtherPartyId: string | null;
  messagesByParty: Record<string, ChatMessageRecord[]>;
  hasMoreByParty: Record<string, boolean>;
  isFetchingOlderByParty: Record<string, boolean>;
  unreadCount: number;
  unreadCountStatus: "idle" | "pending";
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatState = {
  conversations: [],
  activeOtherPartyId: null,
  messagesByParty: {},
  hasMoreByParty: {},
  isFetchingOlderByParty: {},
  unreadCount: 0,
  unreadCountStatus: "idle",
  isLoading: false,
  error: null,
};

export const getChatInboxThunk = createAsyncThunk<
  ChatConversation[],
  { force?: boolean } | void,
  { state: RootState; rejectValue: string }
>(
  "chat/getInbox",
  (_, { rejectWithValue }) => {
    return GetChatInbox()
      .then((res) => res.data.conversations)
      .catch((err: any) => rejectWithValue(err.message));
  },
  {
    condition: (param, { getState }) => {
      const { chat } = getState();
      const force = typeof param === "object" && param?.force;
      if (force) return true;
      return chat.conversations.length === 0;
    },
  }
);

export const getChatMessagesThunk = createAsyncThunk<
  { recipientId: string; messages: ChatMessageRecord[]; hasMore: boolean; isOlder?: boolean },
  { recipientId: string; limit?: number; offset?: number; isOlder?: boolean; force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "chat/getMessages",
  ({ recipientId, limit = 50, offset = 0, isOlder }, { dispatch, getState, rejectWithValue }) => {
    const { auth } = getState();
    const getUserPromise = auth.user
      ? Promise.resolve(auth.user)
      : GetProfile().then((res) => res.data);

    return getUserPromise
      .then((user) => {
        const currentUserId = user.id;
        const role = auth.role || storageService.getRole();
        const studentId = role === "teacher" ? recipientId : currentUserId;
        const teacherId = role === "teacher" ? currentUserId : recipientId;

        const limitClause = offset > 0 ? `limit ${offset}, ${limit}` : `limit ${limit}`;
        const selectQuery = `select id, Message, Sender_Type, Is_Read, Read_At, Created_Time from Chats where Student = '${studentId}' and Teacher = '${teacherId}' order by Created_Time desc ${limitClause}`;

        return GetChatMessages({ selectQuery }, { skipLoader: isOlder }).then((res) => {
          const rawMsgs = res.data.records;
          const hasMore = res.data.info ? res.data.info.more_records : rawMsgs.length >= limit;

          const msgs = [...rawMsgs].sort(
            (a, b) => new Date(a.Created_Time).getTime() - new Date(b.Created_Time).getTime()
          );

          if (!isOlder) {
            dispatch(markReadThunk({ otherPartyId: recipientId }));
          }

          return {
            recipientId,
            messages: msgs,
            hasMore,
            isOlder,
          };
        });
      })
      .catch((err: any) => rejectWithValue(err.message));
  },
  {
    condition: ({ recipientId, isOlder, force }, { getState }) => {
      if (force) return true;
      const { chat } = getState();
      if (isOlder) {
        if (chat.hasMoreByParty[recipientId] === false) return false;
        if (chat.isFetchingOlderByParty[recipientId]) return false;
        return true;
      }
      return !chat.messagesByParty[recipientId];
    },
  }
);

export const sendChatMessageThunk = createAsyncThunk<
  SendChatMessageRequest,
  SendChatMessageRequest,
  { state: RootState; rejectValue: string }
>("chat/sendMessage", (req, { dispatch, rejectWithValue }) => {
  return SendChatMessage(req)
    .then(() => {
      dispatch(getChatMessagesThunk({ recipientId: req.recipientId, force: true }));
      dispatch(getChatInboxThunk({ force: true }));
      return req;
    })
    .catch((err: any) => rejectWithValue(err.message));
});

export const markReadThunk = createAsyncThunk<
  string,
  MarkReadRequest,
  { state: RootState; rejectValue: string }
>("chat/markRead", (req, { rejectWithValue }) => {
  return MarkMessagesRead(req)
    .then(() => req.otherPartyId)
    .catch((err: any) => rejectWithValue(err.message));
});

export const getUnreadCountThunk = createAsyncThunk<
  number,
  { force?: boolean } | void,
  { state: RootState; rejectValue: string }
>(
  "chat/getUnreadCount",
  (_, { rejectWithValue }) => {
    return GetUnreadChatCount({ skipLoader: true })
      .then((res) => res.data.unreadCount)
      .catch((err: any) => rejectWithValue(err.message));
  },
  {
    condition: (param, { getState }) => {
      const force = typeof param === "object" && param?.force;
      if (force) return true;
      const { unreadCountStatus } = getState().chat;
      if (unreadCountStatus === "pending") return false;
      return true;
    },
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setActiveOtherPartyId: (state, action) => {
      state.activeOtherPartyId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatInboxThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getChatInboxThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
        if (action.payload.length > 0) {
          const exists = action.payload.some((c) => c.otherPartyId === state.activeOtherPartyId);
          if (!state.activeOtherPartyId || !exists) {
            state.activeOtherPartyId = action.payload[0].otherPartyId;
          }
        }
      })
      .addCase(getChatInboxThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getChatMessagesThunk.pending, (state, action) => {
        const { isOlder, recipientId } = action.meta.arg;
        if (isOlder) {
          state.isFetchingOlderByParty[recipientId] = true;
        } else {
          state.isLoading = true;
        }
      })
      .addCase(getChatMessagesThunk.fulfilled, (state, action) => {
        const { recipientId, messages, hasMore, isOlder } = action.payload;
        if (isOlder) {
          state.isFetchingOlderByParty[recipientId] = false;
          const existing = state.messagesByParty[recipientId] || [];
          const existingIds = new Set(existing.map((m) => m.id));
          const newMsgs = messages.filter((m) => !existingIds.has(m.id));
          state.messagesByParty[recipientId] = [...newMsgs, ...existing];
          state.hasMoreByParty[recipientId] = hasMore;
        } else {
          state.isLoading = false;
          state.isFetchingOlderByParty[recipientId] = false;
          state.messagesByParty[recipientId] = messages;
          state.hasMoreByParty[recipientId] = hasMore;
        }
      })
      .addCase(getChatMessagesThunk.rejected, (state, action) => {
        const { recipientId } = action.meta.arg;
        state.isLoading = false;
        if (recipientId) {
          state.isFetchingOlderByParty[recipientId] = false;
        }
        state.error = action.payload ?? null;
      })
      .addCase(markReadThunk.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c.otherPartyId === action.payload);
        if (conv) {
          state.unreadCount = Math.max(0, state.unreadCount - conv.unreadCount);
          conv.unreadCount = 0;
        }
      })
      .addCase(getUnreadCountThunk.pending, (state) => {
        state.unreadCountStatus = "pending";
      })
      .addCase(getUnreadCountThunk.fulfilled, (state, action) => {
        state.unreadCountStatus = "idle";
        state.unreadCount = action.payload;
      })
      .addCase(getUnreadCountThunk.rejected, (state) => {
        state.unreadCountStatus = "idle";
      })
      .addCase(logout, (state) => {
        state.conversations = [];
        state.activeOtherPartyId = null;
        state.messagesByParty = {};
        state.hasMoreByParty = {};
        state.isFetchingOlderByParty = {};
        state.unreadCount = 0;
        state.isLoading = false;
        state.error = null;
      });
  },
});


export const { setActiveOtherPartyId } = chatSlice.actions;
export default chatSlice.reducer;
